import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, CircularProgress,
  Alert, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  Box, Switch, Paper, Divider, Slider
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MergeIcon from '@mui/icons-material/Merge';
import { booksAPI } from '../../services/api';

// Data source providers with labels and descriptions
const PROVIDERS = {
  'hybrid': {
    value: 'hybrid',
    label: 'Hybrid (Recommended)',
    icon: <MergeIcon fontSize="small" />,
    description: 'Combines data from multiple sources for best results'
  }
};

const BatchIsbnDialog = ({ open, onClose, onSuccess }) => {
  const [isbnInput, setIsbnInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState('hybrid');
  const [parallelJobs, setParallelJobs] = useState(5);
  const [autoPublish, setAutoPublish] = useState(true);
  const [jobId, setJobId] = useState(null);
  const [jobSubmitted, setJobSubmitted] = useState(false);

  const handleIsbnInputChange = (e) => {
    setIsbnInput(e.target.value);
  };

  const handleProviderChange = (e) => {
    setProvider(e.target.value);
  };

  const handleAutoPublishChange = (e) => {
    setAutoPublish(e.target.checked);
  };

  const handleParallelJobsChange = (event, newValue) => {
    setParallelJobs(newValue);
  };

  const parseIsbnList = () => {
    // Split by newlines, commas or spaces, trim whitespace, and filter out empty entries
    return isbnInput
      .split(/[\n,\s]+/)
      .map(item => item.trim().replace(/[^\w]/g, '')) // Only remove non-alphanumeric chars
      .filter(item => item.length > 0);
  };

  const handleSubmit = async () => {
    const isbns = parseIsbnList();
    
    if (isbns.length === 0) {
      setError('Please enter at least one valid ISBN');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Call the batch ISBN API endpoint
      const response = await booksAPI.createBooksFromBatchIsbns({
        isbns,
        parallelJobs,
        autoPublish
      });
      
      // Extract job ID from response
      const jobId = response?.data?.jobId;
      
      if (jobId) {
        setJobId(jobId);
        setJobSubmitted(true);
        
        // Notify parent component of success
        if (onSuccess) {
          onSuccess({
            jobId,
            isbnCount: isbns.length,
            message: `Batch processing job started with ${isbns.length} ISBNs`
          });
        }
      } else {
        throw new Error('Invalid response format from API');
      }
      
    } catch (error) {
      console.error('Error submitting batch ISBNs:', error);
      setError(
        error.response?.data?.message || 
        'Failed to submit batch ISBNs. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setIsbnInput('');
    setError(null);
    setJobId(null);
    setJobSubmitted(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => !loading && handleClose()} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <LibraryBooksIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Batch Import Books by ISBN</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!jobSubmitted ? (
          // Initial form for submitting batch ISBNs
          <>
            <Typography variant="body2" color="text.secondary" paragraph>
              Enter multiple ISBNs (one per line or comma-separated) to create books in a background job.
              The system will process these ISBNs asynchronously and create books for each valid ISBN.
            </Typography>
            
            <TextField
              autoFocus
              margin="dense"
              id="isbn-batch"
              label="ISBN List"
              placeholder="Enter multiple ISBNs (one per line or comma-separated)"
              multiline
              rows={8}
              fullWidth
              variant="outlined"
              value={isbnInput}
              onChange={handleIsbnInputChange}
              disabled={loading}
              sx={{ my: 2 }}
            />
            
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Processing Options</Typography>
              
              <FormControl component="fieldset" sx={{ my: 2, width: '100%' }}>
                <FormLabel component="legend">Data Source</FormLabel>
                <RadioGroup
                  name="provider"
                  value={provider}
                  onChange={handleProviderChange}
                >
                  {Object.values(PROVIDERS).map((providerOption) => (
                    <FormControlLabel 
                      key={providerOption.value}
                      value={providerOption.value} 
                      control={<Radio size="small" />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ mr: 1 }}>{providerOption.icon}</Box>
                          <Box>
                            <Typography variant="body2">{providerOption.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {providerOption.description}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      disabled={loading} 
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <Typography id="parallel-jobs-slider" gutterBottom>
                  Parallel Jobs: {parallelJobs}
                </Typography>
                <Slider
                  value={parallelJobs}
                  onChange={handleParallelJobsChange}
                  aria-labelledby="parallel-jobs-slider"
                  valueLabelDisplay="auto"
                  step={1}
                  marks
                  min={1}
                  max={10}
                  disabled={loading}
                />
                <Typography variant="caption" color="text.secondary">
                  Number of ISBNs to process simultaneously. Higher values are faster but may cause rate limiting.
                </Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={autoPublish}
                    onChange={handleAutoPublishChange}
                    color="primary"
                    disabled={loading}
                  />
                }
                label="Automatically publish books after creation"
              />
            </Paper>
          </>
        ) : (
          // Success message after job is submitted
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Batch processing job submitted successfully!
            </Alert>
            
            <Typography variant="h6" gutterBottom>
              Job ID: {jobId}
            </Typography>
            
            <Typography variant="body1" paragraph>
              Your batch ISBN import job has been submitted. The system will process the ISBNs in the background.
              You can check the status of this job in the Jobs section.
            </Typography>
            
            <Typography variant="body2" color="text.secondary">
              Books will be created and {autoPublish ? 'automatically published' : 'saved as drafts'} as they are processed.
              This may take several minutes depending on the number of ISBNs submitted.
            </Typography>
          </Paper>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!jobSubmitted ? (
          // Initial actions
          <>
            <Button 
              onClick={handleClose} 
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              color="primary" 
              variant="contained" 
              disabled={loading || !isbnInput.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
            >
              {loading ? 'Submitting...' : 'Submit Batch Job'}
            </Button>
          </>
        ) : (
          // Actions after job is submitted
          <Button 
            onClick={handleClose} 
            color="primary" 
            variant="contained"
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BatchIsbnDialog;
